import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { ExcelData, SchemaData } from '../../pages/ExcelUploadPage';
import SchemaLoader from './SchemaLoader';

interface LegCountSelectorProps {
  legCount: number;
  onLegCountChange: (count: number) => void;
  excelData: ExcelData;
  savedSchemas?: SchemaData[];
  loadedSchemaName?: string;
  onSchemaLoad?: (schema: SchemaData) => void;
  onSchemaClear?: () => void;
  onSchemaDelete?: (schema: SchemaData) => Promise<void>;
}

interface LegOption {
  count: number;
  label: string;
  description: string;
  route: string[];
}

const LEG_OPTIONS: LegOption[] = [
  { count: 1, label: '1 Leg — Direct', description: 'Single origin → destination segment', route: ['Origin', 'Destination'] },
  { count: 2, label: '2 Legs — One transfer', description: 'Two consecutive transport segments', route: ['Origin', 'Transfer 1', 'Destination'] },
  { count: 3, label: '3 Legs — Two transfers', description: 'Three consecutive segments', route: ['Origin', 'Transfer 1', 'Transfer 2', 'Destination'] },
  { count: 4, label: '4 Legs — Three transfers', description: 'Four consecutive segments', route: ['Origin', 'T1', 'T2', 'T3', 'Destination'] },
  { count: 5, label: '5 Legs — Four transfers', description: 'Five consecutive segments', route: ['Origin', 'T1', 'T2', 'T3', 'T4', 'Destination'] },
];

export const LegCountSelector: React.FC<LegCountSelectorProps> = ({
  legCount,
  onLegCountChange,
  excelData,
  savedSchemas = [],
  loadedSchemaName,
  onSchemaLoad,
  onSchemaClear,
  onSchemaDelete,
}) => {
  const loadedSchema = savedSchemas.find(s => s.name === loadedSchemaName);
  const schemaLegCount = loadedSchema?.legCount ?? null;
  const legCountDiffersFromSchema =
    !!loadedSchema && schemaLegCount !== null && schemaLegCount !== legCount;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Configure Transport Legs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Tell us how many transport segments each shipment has. This determines
          which fields you'll need to map.
        </Typography>
      </Box>
{onSchemaLoad && <Divider sx={{ mb: 2 }} />}
      {/* Schema Loader — shown when schemas are available */}
      {onSchemaLoad && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Load a Saved Schema
            </Typography>
            <Tooltip title="Applying a schema will pre-fill your column mappings and configure the leg count automatically." arrow>
              <InfoIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'default' }} />
            </Tooltip>
            <Chip
              label="Optional"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.62rem', borderColor: 'grey.400', color: 'text.secondary' }}
            />
          </Box>
          <SchemaLoader
            schemas={savedSchemas}
            loadedSchemaName={loadedSchemaName}
            onLoad={onSchemaLoad}
            onClear={onSchemaClear}
            onDelete={onSchemaDelete}
            title=""
            subtitle="Applying a schema will pre-fill your column mappings and configure the leg count automatically."
            emptyMessage="No inline schemas saved yet. After completing your first mapping, save it as a schema to speed up future uploads."
          />
          {/* Contextual hints about schema ↔ leg count relationship */}
          {savedSchemas.length > 0 && !loadedSchemaName && (
            <Alert severity="info" icon={false} sx={{ mt: 1.5, py: 0.75, fontSize: '0.78rem' }}>
              You can skip schema selection and configure legs manually — you'll be prompted to save your mapping as a new schema before processing.
            </Alert>
          )}
          {loadedSchemaName && !legCountDiffersFromSchema && (
            <Alert severity="success" icon={false} sx={{ mt: 1.5, py: 0.75, fontSize: '0.78rem' }}>
              Schema loaded. Leg count and mappings have been pre-filled. You can adjust the leg count below — it will be saved when you update the schema.
            </Alert>
          )}
          {loadedSchemaName && legCountDiffersFromSchema && (
            <Alert severity="warning" icon={false} sx={{ mt: 1.5, py: 0.75, fontSize: '0.78rem' }}>
              You changed the leg count from the schema's {schemaLegCount} leg{schemaLegCount === 1 ? '' : 's'}. This change will be applied when you save or update the schema before processing.
            </Alert>
          )}
        </Box>
      )}

      {onSchemaLoad && <Divider sx={{ mb: 2 }} />}

      {/* Leg Count Selector */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Number of legs per shipment
          </Typography>
          <Tooltip title="Count the number of origin → destination segments in your data" arrow>
            <InfoIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'default' }} />
          </Tooltip>
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel>Legs per shipment</InputLabel>
          <Select
            value={legCount}
            label="Legs per shipment"
            onChange={e => onLegCountChange(Number(e.target.value))}
          >
            {LEG_OPTIONS.map(opt => (
              <MenuItem key={opt.count} value={opt.count} sx={{ py: 1.25 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {opt.label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                    {opt.route.map((stop, idx) => (
                      <React.Fragment key={stop}>
                        <Chip
                          label={stop}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.62rem',
                            bgcolor: idx === 0 || idx === opt.route.length - 1 ? 'primary.main' : 'grey.200',
                            color: idx === 0 || idx === opt.route.length - 1 ? 'white' : 'text.secondary',
                            fontWeight: idx === 0 || idx === opt.route.length - 1 ? 700 : 400,
                          }}
                        />
                        {idx < opt.route.length - 1 && (
                          <Typography variant="caption" sx={{ mx: 0.4, color: 'grey.400', fontSize: '0.7rem' }}>
                            →
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75, fontSize: '0.68rem' }}>
                      ({opt.description})
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
  {onSchemaLoad && <Divider sx={{ mb: 2 }} />}
      {/* Excel Preview */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Your file preview
        </Typography>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 260, borderRadius: 2 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {excelData.headers.map((header, idx) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'grey.100',
                      fontSize: '0.72rem',
                      py: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {excelData.rows.slice(0, 5).map((row, rowIdx) => (
                <TableRow
                  key={row.join('|')}
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  {row.map((cell, cellIdx) => (
                    <TableCell
                      key={excelData.headers[cellIdx]}
                      sx={{ fontSize: '0.72rem', py: 0.75, whiteSpace: 'nowrap' }}
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {excelData.totalRows > 5 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            Showing 5 of {excelData.totalRows} rows
          </Typography>
        )}
      </Box>
    </Box>
  );
};
